import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import AppHeader from '@/components/AppHeader';
import { extractErrorMessage } from '@/lib/errors';
import { Loader2 } from 'lucide-react';

export default function VerifyOTPPage() {
  const { pendingPhone, pendingOtpHint, verifySignupOTP } = useAuth();
  // When the backend is in mock mode (AUTH_OTP_MODE=mock) the register
  // response comes back with an otp_hint. Pre-fill the boxes with it
  // so a tester on a fake phone number doesn't have to look up the
  // code. In real Twilio mode there is no hint and the fields start
  // empty as normal.
  const initialOtp = pendingOtpHint && pendingOtpHint.length === 6
    ? pendingOtpHint.split('')
    : ['', '', '', '', '', ''];
  const [otp, setOtp] = useState(initialOtp);
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef([]);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    if (!pendingPhone) {
      navigate('/login');
    }
    inputRefs.current[0]?.focus();
  }, [pendingPhone, navigate]);

  const handleChange = (index, value) => {
    if (!/^[0-9]*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when complete
    if (index === 5 && value) {
      const fullOtp = newOtp.join('');
      if (fullOtp.length === 6) {
        handleVerify(fullOtp);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (/^[0-9]+$/.test(pastedData)) {
      const newOtp = pastedData.split('').concat(Array(6 - pastedData.length).fill(''));
      setOtp(newOtp);
      if (pastedData.length === 6) {
        handleVerify(pastedData);
      }
    }
  };

  const handleVerify = async (otpValue) => {
    const code = otpValue || otp.join('');
    if (code.length !== 6) {
      toast({ title: t('auth.verifyOTP.errors.incomplete'), variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      await verifySignupOTP(pendingPhone, code);
      toast({ title: t('auth.verifyOTP.verified') });
      // Full page reload so auth state is fresh in every mounted context.
      window.location.href = '/dashboard';
    } catch (error) {
      toast({
        title: extractErrorMessage(error, t('auth.verifyOTP.errors.invalid')),
        variant: 'destructive',
      });
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Shared branded header */}
      <AppHeader />

      <div className="flex-1 flex flex-col p-6">
        {/* Header */}
        <div className="text-center mt-6 mb-10">
          <span className="text-6xl mb-4 block">🔒</span>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.verifyOTP.title')}</h1>
          <p className="text-gray-600">
            {t('auth.verifyOTP.subtitle')}<br />
            <span className="font-semibold text-farm-primary">{pendingPhone}</span>
          </p>
        </div>

      {/* Dev-mode banner — only visible when backend returned an otp_hint
          (AUTH_OTP_MODE=mock). Never appears in production Twilio mode. */}
      {pendingOtpHint && (
        <div className="mb-4 p-3 rounded-lg border border-blue-200 bg-blue-50 text-center" data-testid="otp-mock-banner">
          <p className="text-xs font-semibold text-blue-800 uppercase tracking-wide">Dev environment</p>
          <p className="text-sm text-blue-700 mt-0.5">
            OTP is pre-filled for testing — just tap Verify.
          </p>
        </div>
      )}

      {/* OTP Input */}
      <div className="flex justify-between gap-2 mb-6" onPaste={handlePaste}>
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-200 rounded-lg bg-white focus:border-farm-primary focus:outline-none transition-colors"
            data-testid={`otp-input-${index}`}
          />
        ))}
      </div>

      {/* Verify Button */}
      <Button
        onClick={() => handleVerify()}
        disabled={loading || otp.some((d) => !d)}
        className="w-full h-14 bg-farm-primary hover:bg-farm-primary-dark text-white font-semibold text-lg rounded-xl mb-4"
        data-testid="verify-otp-btn"
      >
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t('auth.verifyOTP.submit')}
      </Button>

      <p className="text-center text-sm text-gray-500 mt-4">
        {t('auth.verifyOTP.resendHint')}
      </p>
      </div>
    </div>
  );
}
